import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

const POLL_INTERVAL_MS = 30_000

const SENSOR_CONFIG_COLUMNS =
	'id, customer_id, sensor_id, sensor_location, delivery_enabled, realtime_enabled, show_alkalinity, show_ph, show_temperature, show_conductivity, customer:customers(id, name)'

/**
 * Fetches sensor_configs across all customers (admin-only; relies on RLS to
 * permit access). Returns sensors grouped by customer (alphabetical) and as a
 * flat list for selectors.
 *
 * When includeLatest is true (default), also loads one latest measurement per
 * sensor via get_latest_measurements RPC and refreshes on a ~30s poll / window focus.
 * When false, only loads selector metadata (no measurements, no polling).
 *
 * @param {{ enabled?: boolean, includeLatest?: boolean }} options
 */
export function useAllCustomersSensors({ enabled = true, includeLatest = true } = {}) {
	const [customerGroups, setCustomerGroups] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const initialFetchDone = useRef(false)

	const fetchAll = useCallback(async () => {
		if (!enabled) return

		try {
			if (!initialFetchDone.current) setLoading(true)
			setError(null)

			const { data: configs, error: configError } = await supabase
				.from('sensor_configs')
				.select(SENSOR_CONFIG_COLUMNS)
				.eq('delivery_enabled', true)
				.eq('realtime_enabled', true)
				.order('sensor_location')

			if (configError) throw configError

			if (!configs || configs.length === 0) {
				setCustomerGroups([])
				return
			}

			let latestByKey = new Map()
			if (includeLatest) {
				const { data: latestRows, error: latestError } = await supabase.rpc(
					'get_latest_measurements'
				)

				if (latestError) {
					console.warn('[use-all-customers-sensors] Error fetching latest:', latestError)
				}

				latestByKey = new Map(
					(latestRows || []).map((row) => [`${row.customer_id}:${row.sensor_id}`, row])
				)
			}

			const sensorsWithData = configs.map((config) => ({
				...config,
				customerName: config.customer?.name || 'Unknown',
				latestMeasurement: includeLatest
					? latestByKey.get(`${config.customer_id}:${config.sensor_id}`) || null
					: null,
			}))

			const byCustomer = {}
			sensorsWithData.forEach((sensor) => {
				const cid = sensor.customer_id
				if (!byCustomer[cid]) {
					byCustomer[cid] = { customer: sensor.customer, sensors: [] }
				}
				byCustomer[cid].sensors.push(sensor)
			})

			const groups = Object.values(byCustomer).sort((a, b) =>
				a.customer.name.localeCompare(b.customer.name)
			)

			setCustomerGroups(groups)
		} catch (err) {
			console.error('[use-all-customers-sensors] Error:', err)
			setError(err.message)
		} finally {
			initialFetchDone.current = true
			setLoading(false)
		}
	}, [enabled, includeLatest])

	useEffect(() => {
		if (!enabled) {
			setCustomerGroups([])
			setLoading(false)
			return
		}
		initialFetchDone.current = false
		fetchAll()
	}, [enabled, fetchAll])

	// Polling — only when latest values are needed
	useEffect(() => {
		if (!enabled || !includeLatest) return
		const id = setInterval(fetchAll, POLL_INTERVAL_MS)
		return () => clearInterval(id)
	}, [enabled, includeLatest, fetchAll])

	// Window focus refresh — only when latest values are needed
	useEffect(() => {
		if (!enabled || !includeLatest) return
		const onFocus = () => {
			if (initialFetchDone.current) fetchAll()
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [enabled, includeLatest, fetchAll])

	// Flat list for use in Autocomplete selectors (dashboard / historical)
	const allSensorsList = customerGroups.flatMap((g) => g.sensors)

	return { customerGroups, allSensorsList, loading, error, refetch: fetchAll }
}
